import { createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import strService, {
  STRDashboardStats,
  STRDailySummary,
  STRRule,
  STRAlert,
  STRAccountMonitoring,
  STRAccountFlags,
  STRExcelExportParams,
  STREmailParams,
} from './strService';

// Dashboard Stats
export const getSTRDashboardStatsThunk = createAsyncThunk(
  'str/getDashboardStats',
  async ({ dateFrom, dateTo }: { dateFrom?: string; dateTo?: string } = {}) => {
    try {
      const data = await strService.getDashboardStats({ dateFrom, dateTo });
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch dashboard statistics');
      throw error;
    }
  }
);

export const getSTRDailySummaryThunk = createAsyncThunk(
  'str/getDailySummary',
  async (date: string) => {
    try {
      const data = await strService.getDailySummary({ date });
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch daily summary');
      throw error;
    }
  }
);

export const getSTRWeeklySummaryThunk = createAsyncThunk(
  'str/getWeeklySummary',
  async (startDate: string) => {
    try {
      const data = await strService.getWeeklySummary({ startDate });
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch weekly summary');
      throw error;
    }
  }
);

// Rule Configuration
export const getAllSTRRulesThunk = createAsyncThunk('str/getAllRules', async () => {
  try {
    const data = await strService.getAllRules();
    return data;
  } catch (error: any) {
    message.error(error.response?.data?.message || 'Failed to fetch STR rules');
    throw error;
  }
});

export const getSTRRuleThunk = createAsyncThunk('str/getRule', async (ruleCode: string) => {
  try {
    const data = await strService.getRule({ ruleCode });
    return data;
  } catch (error: any) {
    message.error(error.response?.data?.message || 'Failed to fetch STR rule');
    throw error;
  }
});

export const updateSTRRuleThresholdsThunk = createAsyncThunk(
  'str/updateRuleThresholds',
  async ({ ruleCode, thresholds }: { ruleCode: string; thresholds: Record<string, any> }) => {
    try {
      await strService.updateRuleThresholds({ ruleCode, thresholds });
      message.success('Rule thresholds updated successfully');
      return { ruleCode, thresholds };
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update rule thresholds');
      throw error;
    }
  }
);

export const toggleSTRRuleThunk = createAsyncThunk(
  'str/toggleRule',
  async ({ ruleCode, isEnabled }: { ruleCode: string; isEnabled: boolean }) => {
    try {
      await strService.toggleRule({ ruleCode, isEnabled });
      message.success(`Rule ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      return { ruleCode, isEnabled };
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to toggle rule');
      throw error;
    }
  }
);

// Alert Management
export const getSTRAlertsThunk = createAsyncThunk(
  'str/getAlerts',
  async (params: {
    agreementId?: string;
    ruleCode?: string;
    severity?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      const data = await strService.getAlerts(params);
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch STR alerts');
      throw error;
    }
  }
);

export const hideSTRAlertThunk = createAsyncThunk(
  'str/hideAlert',
  async ({ alertId, reason }: { alertId: string; reason: string }) => {
    try {
      await strService.hideAlert({ alertId, reason });
      message.success('Alert hidden successfully');
      return alertId;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to hide alert');
      throw error;
    }
  }
);

export const showSTRAlertThunk = createAsyncThunk('str/showAlert', async (alertId: string) => {
  try {
    await strService.showAlert({ alertId });
    message.success('Alert shown successfully');
    return alertId;
  } catch (error: any) {
    message.error(error.response?.data?.message || 'Failed to show alert');
    throw error;
  }
});

export const resolveSTRAlertThunk = createAsyncThunk(
  'str/resolveAlert',
  async (alertId: string) => {
    try {
      await strService.resolveAlert({ alertId });
      message.success('Alert resolved successfully');
      return alertId;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to resolve alert');
      throw error;
    }
  }
);

export const bulkHideSTRAlertsThunk = createAsyncThunk(
  'str/bulkHideAlerts',
  async ({ alertIds, reason }: { alertIds: string[]; reason: string }) => {
    try {
      await strService.bulkHideAlerts({ alertIds, reason });
      message.success(`${alertIds.length} alerts hidden successfully`);
      return alertIds;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to hide alerts');
      throw error;
    }
  }
);

export const hideAllAccountSTRAlertsThunk = createAsyncThunk(
  'str/hideAllAccountAlerts',
  async ({ agreementId, reason }: { agreementId: string; reason: string }) => {
    try {
      await strService.hideAllAccountAlerts({ agreementId, reason });
      message.success('All account alerts hidden successfully');
      return agreementId;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to hide account alerts');
      throw error;
    }
  }
);

export const hideRuleSTRAlertsForAccountThunk = createAsyncThunk(
  'str/hideRuleAlertsForAccount',
  async ({
    agreementId,
    ruleCode,
    reason,
  }: {
    agreementId: string;
    ruleCode: string;
    reason: string;
  }) => {
    try {
      await strService.hideRuleAlertsForAccount({ agreementId, ruleCode, reason });
      message.success('Rule alerts hidden for account successfully');
      return { agreementId, ruleCode };
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to hide rule alerts');
      throw error;
    }
  }
);

export const getSTRAlertStatisticsThunk = createAsyncThunk(
  'str/getAlertStatistics',
  async ({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) => {
    try {
      const data = await strService.getAlertStatistics({ dateFrom, dateTo });
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch alert statistics');
      throw error;
    }
  }
);

// Account Monitoring
export const getSTRAccountMonitoringStatusThunk = createAsyncThunk(
  'str/getAccountMonitoringStatus',
  async (agreementId: string) => {
    try {
      const data = await strService.getAccountMonitoringStatus({ agreementId });
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch account monitoring status');
      throw error;
    }
  }
);

export const searchSTRAccountMonitoringByAgreementNoThunk = createAsyncThunk(
  'str/searchAccountMonitoringByAgreementNo',
  async (agreementNo: string) => {
    try {
      const data = await strService.searchAccountMonitoringByAgreementNo({ agreementNo });
      return data;
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to search account by agreement number'
      );
      throw error;
    }
  }
);

export const disableSTRAccountMonitoringThunk = createAsyncThunk(
  'str/disableAccountMonitoring',
  async ({ agreementId, reason }: { agreementId: string; reason: string }) => {
    try {
      await strService.disableAccountMonitoring({ agreementId, reason });
      message.success('Account monitoring disabled successfully');
      return agreementId;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to disable account monitoring');
      throw error;
    }
  }
);

export const enableSTRAccountMonitoringThunk = createAsyncThunk(
  'str/enableAccountMonitoring',
  async ({ agreementId, reason }: { agreementId: string; reason: string }) => {
    try {
      await strService.enableAccountMonitoring({ agreementId, reason });
      message.success('Account monitoring enabled successfully');
      return agreementId;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to enable account monitoring');
      throw error;
    }
  }
);

export const disableSTRAccountRulesThunk = createAsyncThunk(
  'str/disableAccountRules',
  async ({
    agreementId,
    rules,
    reason,
  }: {
    agreementId: string;
    rules: string[];
    reason: string;
  }) => {
    try {
      await strService.disableAccountRules({ agreementId, rules, reason });
      message.success('Account rules disabled successfully');
      return { agreementId, rules };
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to disable account rules');
      throw error;
    }
  }
);

export const enableSTRAccountRulesThunk = createAsyncThunk(
  'str/enableAccountRules',
  async ({
    agreementId,
    rules,
    reason,
  }: {
    agreementId: string;
    rules: string[];
    reason: string;
  }) => {
    try {
      await strService.enableAccountRules({ agreementId, rules, reason });
      message.success('Account rules enabled successfully');
      return { agreementId, rules };
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to enable account rules');
      throw error;
    }
  }
);

export const unflagSTRAccountThunk = createAsyncThunk(
  'str/unflagAccount',
  async ({
    agreementId,
    reason,
    rulesToEnable,
  }: {
    agreementId: string;
    reason: string;
    rulesToEnable?: string[];
  }) => {
    try {
      await strService.unflagAccount({ agreementId, reason, rulesToEnable });
      message.success('Account unflagged successfully');
      return agreementId;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to unflag account');
      throw error;
    }
  }
);

// Account Flags
export const getSTRAccountFlagsThunk = createAsyncThunk(
  'str/getAccountFlags',
  async (agreementId: string) => {
    try {
      const data = await strService.getAccountFlags({ agreementId });
      return data;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch account flags');
      throw error;
    }
  }
);

// Excel Export
export const downloadSTRExcelReportThunk = createAsyncThunk(
  'str/downloadExcelReport',
  async (params: STRExcelExportParams) => {
    try {
      const blob = await strService.downloadExcelReport(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `STR_Report_${params.dateFrom}_to_${params.dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Excel report downloaded successfully');
      return params;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to download Excel report');
      throw error;
    }
  }
);

export const sendSTRExcelEmailThunk = createAsyncThunk(
  'str/sendExcelEmail',
  async (params: STREmailParams) => {
    try {
      await strService.sendExcelEmail(params);
      message.success('Excel report sent via email successfully');
      return params;
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send Excel report via email');
      throw error;
    }
  }
);

// Daily Summary Email
export const sendSTRDailySummaryEmailThunk = createAsyncThunk(
  'str/sendDailySummaryEmail',
  async ({
    date,
    recipients,
    includeDetailedBreakdown,
    includeCharts,
    customMessage,
    priority,
    scheduleTime,
  }: {
    date: string;
    recipients: string[];
    includeDetailedBreakdown?: boolean;
    includeCharts?: boolean;
    customMessage?: string;
    priority?: 'LOW' | 'NORMAL' | 'HIGH';
    scheduleTime?: string;
  }) => {
    try {
      const result = await strService.sendDailySummaryEmail({
        date,
        recipients,
        includeDetailedBreakdown,
        includeCharts,
        customMessage,
        priority,
        scheduleTime,
      });

      if (scheduleTime) {
        message.success(
          `Daily summary email scheduled for ${new Date(scheduleTime).toLocaleString()}`
        );
      } else {
        message.success('Daily summary email sent successfully');
      }

      return { date, recipients, result };
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send daily summary email');
      throw error;
    }
  }
);
